import { useDebounce } from "PFCore/helpers/use_debounce";
import { NormalizedKeyword } from "PFReducers/search_reducer/helpers/normalize_keywords";
import { useCallback, useEffect } from "react";
import { Location, NavigateFunction } from "react-router";

import { allTabs, matchesTab } from "../tabs_constants";

type SearchUrlSyncArgs = {
  navigate: NavigateFunction;
  location: Location;
  term: string;
  trimmedTerm: string;
  tab: string;
  searchActivityId?: number;
  searchActivityFetchCounter: number;
  matchesPending: boolean;
  choosenKeywords: NormalizedKeyword[];
};

export const useSearchUrlSync = ({
  navigate,
  location,
  term,
  trimmedTerm,
  tab,
  searchActivityId,
  searchActivityFetchCounter,
  matchesPending,
  choosenKeywords
}: SearchUrlSyncArgs): void => {
  const updateUrl = useCallback(() => {
    if (searchActivityFetchCounter === 0) {
      return;
    }
    const encodedTerm = window.encodeURI(term || "").replace(/\//g, "%2f");
    const safeTab = allTabs.includes(tab) ? tab : "all";
    // If there is no choosenKeywords then the searchActivity would not be reloadable.
    // You cannot save a task without custom_fields - we dont even atempt (see fetchMatches)
    // We still keep the id in memory so we can reuse the task but if user reloads the page
    // the id is lost.
    //
    //const safeId = choosenKeywords.length > 0 && searchActivityId ? searchActivityId : "new";
    const safeId = searchActivityId || "new";

    if (safeTab === matchesTab && !searchActivityId && matchesPending) {
      // when you load the task for the first time and no choosenKeywords then
      // we don't want to update the url (it'd update to "new" and then back to
      // the proper id after the fetch
      return;
    }

    if (window.encodeURI(location.pathname) !== `/search/${safeId}/${safeTab}/${encodedTerm}`) {
      navigate(`/search/${safeId}/${safeTab}/${encodedTerm}`);
    }
  }, [searchActivityFetchCounter, navigate, location.pathname, tab, searchActivityId, matchesPending, term]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(useDebounce(updateUrl, 10), [
    tab,
    trimmedTerm,
    searchActivityId,
    choosenKeywords,
    matchesPending,
    searchActivityFetchCounter
  ]);
};
